'use client';

import { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../../libs/utils';
import { semanticColor } from '../../theme/semanticColor';
import { IconButton, IconButtonProps } from '../button';
import { Icon } from '../icon/Icon';

// 조건부 타입: onCloseAction이 있으면 closeButtonVariant를 필수로, 없으면 optional로
type TopBannerPropsBase = {
  isOpen: boolean;
  text: ReactNode;
  leadingElement?: ReactNode;
  containerMaxWidth?: 'medium' | 'large';
} & Omit<HTMLAttributes<HTMLDivElement>, 'onClick'>;

type TopBannerPropsWithBannerAction = TopBannerPropsBase & {
  onClick?: () => void;
  iconColor?: string;
  onCloseAction?: never;
  trailingElement?: never;
  closeButtonVariant?: never;
};

type TopBannerPropsWithoutBannerAction = TopBannerPropsBase & {
  onCloseAction?: () => void;
  closeButtonVariant?: IconButtonProps['variant'];
  trailingElement?: ReactNode;
  onClick?: never;
  iconColor?: never;
};

type TopBannerProps = TopBannerPropsWithBannerAction | TopBannerPropsWithoutBannerAction;

export const TopBanner = ({
  className,
  isOpen,
  text,
  trailingElement,
  leadingElement,
  containerMaxWidth = 'medium',
  iconColor = semanticColor.neutral.foreground['inverse-main'],
  onClick,
  onCloseAction,
  closeButtonVariant = 'inverse'
}: TopBannerProps) => {
  const renderRightIcon = () => {
    if (onCloseAction && onClick) {
      return null;
    } else if (onCloseAction && closeButtonVariant) {
      return <IconButton className={'-mr-tiny-large'} size={'xsmall'} variant={closeButtonVariant} iconType={'close'} onClick={onCloseAction} />;
    } else if (onClick) {
      return (
        <div className={'-mr-tiny-small'}>
          <Icon color={iconColor} type={'chevron-right'} size={15} />
        </div>
      );
    }
    return null;
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={cn('py-normal-small bg-info-background-main min-h-[54px] sticky w-full flex justify-center', className, {
        'cursor-pointer': onClick
      })}
      onClick={onClick}
    >
      <div
        className={cn(
          'flex w-full items-center caption-medium-semibold text-neutral-foreground-inverse-main container-padding-medium gap-tiny-large',
          containerMaxWidth === 'large' ? 'container-large' : 'container-medium'
        )}
      >
        {leadingElement}
        <div className={'flex-1'}>{text}</div>
        {trailingElement}
        {renderRightIcon()}
      </div>
    </div>
  );
};
